/**
 * Who signs this application, and with what contact details.
 *
 * Extracted out of `OneSpanESignStep` so the e-signature TABLE and the
 * `bmoData` SUBMISSION are built from one implementation. Two copies of this
 * would drift, and the failure mode of drift is a signing package that doesn't
 * match the summary the advisor approved.
 *
 * The roster is resolved in the browser rather than server-side because the two
 * things it depends on never reach the back end: gating conditions are
 * evaluated here (`evaluateConditions`), and repeatable-section instance values
 * live under `inst:<sectionId>:<idx>:<fieldId>` keys that
 * `buildApplicationSnapshot` doesn't send. The server re-derives everything it
 * can check — see `BmoDataBuilder`.
 */
import { evaluateConditions } from './evaluateConditions';
import { getInstanceCount, qualifiedKey, sectionMinInstances } from './sectionInstances';
import type { SignatoryRole, WorkflowTemplate } from './templateTypes';
import type { FieldValue } from './values';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/** Section placement for a field — enough to build its instance-qualified
 *  value key when the owning section repeats. */
export type FieldSectionInfo = { id: string; repeatable: boolean; minInstances: number };

/** One person in the package: an authored role plus which instance of it. */
export type RosterEntry = { role: SignatoryRole; instanceIdx: number };

/**
 * The signed-in advisor, for the advisor row.
 *
 * Names are carried unjoined because BMO's `signerInfo` takes `firstName` and
 * `lastName` separately (spec §5.4) — `/me` surfaces both off the GAMX advisor
 * profile. `fullName` stays for the table, which shows one name cell.
 */
export type AdvisorIdentity = {
  fullName: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
};

/** What a signer's mapped fields resolved to. */
export type SignerContact = {
  /** Display form — first + last joined, for the summary table. */
  fullName: string;
  firstName: string;
  /** Empty when the config maps no last-name field: many BMO forms collect a
   *  single full-name field, and splitting one apart is guesswork we don't do. */
  lastName: string;
  email: string | null;
  phone: string | null;
  /**
   * Whatever the mapped language field answered, RAW and un-normalized — null
   * when the role maps no language field or the client has not answered it.
   *
   * Deliberately not collapsed to `en` / `fr` here. `BmoDataBuilder` owns the
   * wire format and applies the same fallback for a missing value as for an
   * unrecognized one, so pre-judging here would give the browser a second,
   * drifting opinion about what BMO receives.
   */
  language: string | null;
};

/**
 * Whether this role is expected to sign.
 *
 * The default is TRUE and lives here so nothing compares the flag directly:
 * every role authored before the toggle existed carries no value at all, and
 * only an explicit `false` opts out.
 *
 * A role that opts out is NOT off the roster — it still resolves contact
 * details and still reaches BMO through `applicantInfo`, which is the whole
 * point of the flag (a party named on the application who does not sign it).
 * What it loses is the signing half: no placements to author, no anchor, no
 * `signerInfo` entry. So callers that build the SIGNING package filter on this;
 * `buildESignSubmission`, which feeds both halves, deliberately does not.
 */
export function roleExpectsSignature(role: Pick<SignatoryRole, 'requiresSignature'>): boolean {
  return role.requiresSignature !== false;
}

/** fieldId -> owning section, for every authored field on the template. */
export function buildFieldSections(template: WorkflowTemplate): Map<string, FieldSectionInfo> {
  const map = new Map<string, FieldSectionInfo>();
  for (const step of template.steps) {
    if (step.type !== 'DynamicForm') continue;
    for (const section of step.sections) {
      const info: FieldSectionInfo = {
        id: section.id,
        repeatable: section.repeatable === true,
        minInstances: sectionMinInstances(section),
      };
      for (const row of section.rows) {
        for (const col of row.columns) {
          if (col.field) map.set(col.field.id, info);
        }
      }
    }
  }
  return map;
}

/**
 * The roles included in the signing package for the current answers, in
 * signing order — clients first, the advisor last.
 *
 * Gating conditions decide whether a client signer is included — the same
 * evaluator the form renderer uses for step/section/field visibility. The
 * advisor has no condition UI and is always included.
 *
 * `nothingToSign` drops a signer for the other reason they can have no business
 * in the package: every signature placed for them was itself conditioned out
 * (`rolesWithNothingToSign`). Passed in rather than computed here because it
 * needs the documents and the generated language, neither of which this module
 * sees — and omitted entirely by callers that only care about the roles
 * themselves.
 *
 * The advisor is moved to the end rather than left where the originator
 * authored them: they countersign a package the clients have already signed.
 * `BmoDataBuilder` imposes the same order on the payload it sends — it owns
 * ordering and cannot take the browser's word for it — and this keeps the
 * summary table the advisor approves showing the order BMO will actually use.
 * The sort is stable, so client roles keep their authored order.
 */
export function visibleSignerRoles(
  roles: readonly SignatoryRole[] | undefined,
  values: Record<string, FieldValue>,
  nothingToSign?: ReadonlySet<string>,
): SignatoryRole[] {
  return (roles ?? [])
    .filter(
      (role) =>
        role.type === 'advisor' ||
        (evaluateConditions(role.conditions, role.conditionCombinator, values) &&
          !nothingToSign?.has(role.slug)),
    )
    .sort((a, b) => Number(a.type === 'advisor') - Number(b.type === 'advisor'));
}

/**
 * How many signers one authored role currently stands for.
 *
 * A role whose mapped fields live in a repeatable section represents one
 * signer PER INSTANCE — two Accountholders means two people to sign, each
 * with their own name and email. Anything else is a single signer.
 *
 * A role with `fixedInstanceIndex` set is pinned to exactly one instance —
 * it never auto-multiplies, regardless of the section's live instance
 * count. That's the only way today for two instances of the same
 * repeatable section to each get their own signature placement (see
 * `SignatoryRole.fixedInstanceIndex`).
 */
export function signerInstanceCount(
  role: SignatoryRole,
  sectionByField: Map<string, FieldSectionInfo>,
  values: Record<string, FieldValue>,
): number {
  if (role.type === 'advisor') return 1;
  if (role.fixedInstanceIndex != null) return 1;
  const m = role.fieldMappings;
  // Identity slots only — `languageFieldId` is deliberately excluded. How many
  // people a role stands for is a fact about who they are, not about what
  // language they asked for, and letting a language radio drive the count would
  // multiply a role off a field that names nobody.
  for (const id of [m?.nameFieldId, m?.lastNameFieldId, m?.emailFieldId, m?.phoneFieldId]) {
    if (!id || id === EMPTY_GUID) continue;
    const sec = sectionByField.get(id);
    if (sec?.repeatable) return getInstanceCount(values, sec.id, sec.minInstances);
  }
  return 1;
}

/**
 * Every (role, instance) pair that will be asked to sign, in package order:
 * visible roles expanded by their instance count. Non-repeatable roles produce
 * a single entry with instance 0, so nothing changes for them. A role pinned
 * via `fixedInstanceIndex` produces exactly one entry at that instance.
 *
 * `nothingToSign` is forwarded to {@link visibleSignerRoles} — see there.
 */
export function buildSignerRoster(
  roles: readonly SignatoryRole[] | undefined,
  sectionByField: Map<string, FieldSectionInfo>,
  values: Record<string, FieldValue>,
  nothingToSign?: ReadonlySet<string>,
): RosterEntry[] {
  return visibleSignerRoles(roles, values, nothingToSign).flatMap((role) => {
    if (role.fixedInstanceIndex != null) return [{ role, instanceIdx: role.fixedInstanceIndex }];
    const count = signerInstanceCount(role, sectionByField, values);
    return Array.from({ length: count }, (_, instanceIdx) => ({ role, instanceIdx }));
  });
}

export function resolveSignerContact(
  role: SignatoryRole,
  values: Record<string, FieldValue>,
  sectionByField: Map<string, FieldSectionInfo>,
  instanceIdx: number,
  advisor: AdvisorIdentity,
): SignerContact {
  if (role.type === 'advisor') {
    return {
      fullName: advisor.fullName ?? '',
      firstName: advisor.firstName ?? '',
      lastName: advisor.lastName ?? '',
      email: advisor.email,
      phone: advisor.phone,
      // The advisor has no field mappings to read a language out of, so their
      // invitation follows the application language like it always has.
      language: null,
    };
  }

  const m = role.fieldMappings;
  // `null` is in the union for `languageFieldId`, which is optional on the
  // mappings type rather than an always-present id like the four contact slots.
  const pick = (id: string | null | undefined): string | null => {
    if (!id || id === EMPTY_GUID) return null;
    // A field inside a repeatable section stores each instance under its own
    // qualified key; a plain field is just its id.
    const sec = sectionByField.get(id);
    const v = values[sec?.repeatable ? qualifiedKey(sec.id, instanceIdx, id) : id];
    if (v === null || v === undefined) return null;
    if (typeof v === 'string' || typeof v === 'number') {
      const s = String(v).trim();
      return s ? s : null;
    }
    return null;
  };

  const firstName = pick(m?.nameFieldId);
  const lastName = pick(m?.lastNameFieldId);
  return {
    fullName: [firstName, lastName].filter(Boolean).join(' ') || firstName || '',
    firstName: firstName ?? '',
    lastName: lastName ?? '',
    email: pick(m?.emailFieldId),
    phone: pick(m?.phoneFieldId),
    // Same `pick`, so a language radio living inside the repeatable section
    // this signer came from resolves against THEIR instance rather than the
    // first one's.
    language: pick(m?.languageFieldId),
  };
}
